/**
 * Options REST API
 *
 * Access market data, manage accounts, and trade Binance Options.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
import {
    ConfigurationRestAPI,
    TimeUnit,
    RestApiResponse,
    assertParamExists,
    sendRequest,
    type RequestArgs,
} from '@binance/common';
import type {
    CheckServerTimeResponse,
    ExchangeInformationResponse,
    HistoricalExerciseRecordsResponse,
    IndexPriceResponse,
    KlineCandlestickDataResponse,
    OpenInterestResponse,
    OptionMarkPriceResponse,
    OrderBookResponse,
    RecentBlockTradesListResponse,
    RecentTradesListResponse,
    Ticker24hrPriceChangeStatisticsResponse,
} from '../types';

/**
 * MarketDataApi - axios parameter creator
 */
const MarketDataApiAxiosParamCreator = function (configuration: ConfigurationRestAPI) {
    return {
        /**
         * Test connectivity to the Rest API and get the current server time.
         *
         * Weight(IP): 1
         *
         * @summary Check Server Time
         *
         * @throws {RequiredError}
         */
        checkServerTime: async (): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/eapi/v1/time',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Current exchange trading rules and symbol information
         *
         * Weight(IP): 1
         *
         * @summary Exchange Information
         *
         * @throws {RequiredError}
         */
        exchangeInformation: async (): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/eapi/v1/exchangeInfo',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Get historical exercise records.
         *
         * REALISTIC_VALUE_STRICKEN -> Exercised
         * EXTRINSIC_VALUE_EXPIRED -> Expired OTM
         *
         * Weight(IP): 3
         *
         * @summary Historical Exercise Records
         * @param {string} [underlying] Underlying asset.
         * @param {number | bigint} [startTime] Start Time, e.g 1593511200000
         * @param {number | bigint} [endTime] End Time, e.g 1593512200000
         * @param {number | bigint} [limit] Number of result sets returned
         *
         * @throws {RequiredError}
         */
        historicalExerciseRecords: async (
            underlying?: string,
            startTime?: number | bigint,
            endTime?: number | bigint,
            limit?: number | bigint
        ): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (underlying !== undefined && underlying !== null) {
                localVarQueryParameter['underlying'] = underlying;
            }
            if (startTime !== undefined && startTime !== null) {
                localVarQueryParameter['startTime'] = startTime;
            }
            if (endTime !== undefined && endTime !== null) {
                localVarQueryParameter['endTime'] = endTime;
            }
            if (limit !== undefined && limit !== null) {
                localVarQueryParameter['limit'] = limit;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/eapi/v1/exerciseHistory',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Get spot index price for option underlying.
         *
         * Weight(IP): 1
         *
         * @summary Index Price
         * @param {string} underlying Underlying asset.
         *
         * @throws {RequiredError}
         */
        indexPrice: async (underlying: string): Promise<RequestArgs> => {
            // verify required parameter 'underlying' is not null or undefined
            assertParamExists('indexPrice', 'underlying', underlying);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (underlying !== undefined && underlying !== null) {
                localVarQueryParameter['underlying'] = underlying;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/eapi/v1/index',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Kline/candlestick bars for an option symbol. Klines are uniquely identified by their open time.
         *
         * Weight(IP): 1
         *
         * Notes:
         * - If startTime and endTime are not sent, the most recent klines are returned.
         *
         * @summary Kline/Candlestick Data
         * @param {string} symbol Option trading pair
         * @param {KlineCandlestickDataIntervalEnum} interval Time interval
         * @param {number | bigint} [startTime] Start Time, e.g 1593511200000
         * @param {number | bigint} [endTime] End Time, e.g 1593512200000
         * @param {number | bigint} [limit] Number of result sets returned
         *
         * @throws {RequiredError}
         */
        klineCandlestickData: async (
            symbol: string,
            interval: KlineCandlestickDataIntervalEnum,
            startTime?: number | bigint,
            endTime?: number | bigint,
            limit?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('klineCandlestickData', 'symbol', symbol);
            // verify required parameter 'interval' is not null or undefined
            assertParamExists('klineCandlestickData', 'interval', interval);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (symbol !== undefined && symbol !== null) {
                localVarQueryParameter['symbol'] = symbol;
            }
            if (interval !== undefined && interval !== null) {
                localVarQueryParameter['interval'] = interval;
            }
            if (startTime !== undefined && startTime !== null) {
                localVarQueryParameter['startTime'] = startTime;
            }
            if (endTime !== undefined && endTime !== null) {
                localVarQueryParameter['endTime'] = endTime;
            }
            if (limit !== undefined && limit !== null) {
                localVarQueryParameter['limit'] = limit;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/eapi/v1/klines',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Get open interest for specific underlying asset on specific expiration date.
         *
         * Weight(IP): 0
         *
         * @summary Open Interest
         * @param {string} underlyingAsset Underlying asset.
         * @param {string} expiration expiration date
         *
         * @throws {RequiredError}
         */
        openInterest: async (underlyingAsset: string, expiration: string): Promise<RequestArgs> => {
            // verify required parameter 'underlyingAsset' is not null or undefined
            assertParamExists('openInterest', 'underlyingAsset', underlyingAsset);
            // verify required parameter 'expiration' is not null or undefined
            assertParamExists('openInterest', 'expiration', expiration);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (underlyingAsset !== undefined && underlyingAsset !== null) {
                localVarQueryParameter['underlyingAsset'] = underlyingAsset;
            }
            if (expiration !== undefined && expiration !== null) {
                localVarQueryParameter['expiration'] = expiration;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/eapi/v1/openInterest',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Option mark price and greek info.
         *
         * Weight(IP): 5
         *
         * @summary Option Mark Price
         * @param {string} [symbol] Option trading pair
         *
         * @throws {RequiredError}
         */
        optionMarkPrice: async (symbol?: string): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (symbol !== undefined && symbol !== null) {
                localVarQueryParameter['symbol'] = symbol;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/eapi/v1/mark',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Check orderbook depth on specific symbol
         *
         * Weight: limit         | weight
         * ------------  | ------------
         * 5, 10, 20, 50 | 1
         * 100           | 5
         * 500           | 10
         * 1000          | 20
         *
         * @summary Order Book
         * @param {string} symbol Option trading pair
         * @param {number | bigint} [limit] Default:100 Max:1000.Optional value:[10, 20, 50, 100, 500, 1000]
         *
         * @throws {RequiredError}
         */
        orderBook: async (symbol: string, limit?: number | bigint): Promise<RequestArgs> => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('orderBook', 'symbol', symbol);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (symbol !== undefined && symbol !== null) {
                localVarQueryParameter['symbol'] = symbol;
            }
            if (limit !== undefined && limit !== null) {
                localVarQueryParameter['limit'] = limit;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/eapi/v1/depth',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Get recent block trades
         *
         * Weight(IP): 5
         *
         * @summary Recent Block Trades List
         * @param {string} [symbol] Option trading pair
         * @param {number | bigint} [limit] Number of records
         *
         * @throws {RequiredError}
         */
        recentBlockTradesList: async (
            symbol?: string,
            limit?: number | bigint
        ): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (symbol !== undefined && symbol !== null) {
                localVarQueryParameter['symbol'] = symbol;
            }
            if (limit !== undefined && limit !== null) {
                localVarQueryParameter['limit'] = limit;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/eapi/v1/blockTrades',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Get recent market trades
         *
         * Weight(IP): 5
         *
         * @summary Recent Trades List
         * @param {string} symbol Option trading pair
         * @param {number | bigint} [limit] Number of result sets returned
         *
         * @throws {RequiredError}
         */
        recentTradesList: async (symbol: string, limit?: number | bigint): Promise<RequestArgs> => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('recentTradesList', 'symbol', symbol);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (symbol !== undefined && symbol !== null) {
                localVarQueryParameter['symbol'] = symbol;
            }
            if (limit !== undefined && limit !== null) {
                localVarQueryParameter['limit'] = limit;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/eapi/v1/trades',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Test connectivity to the Rest API.
         *
         * Weight(IP): 1
         *
         * @summary Test Connectivity
         *
         * @throws {RequiredError}
         */
        testConnectivity: async (): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/eapi/v1/ping',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * 24 hour rolling window price change statistics.
         *
         * Weight: 1 for a single symbol; 40 when the symbol parameter is omitted
         *
         * @summary 24hr Ticker Price Change Statistics
         * @param {string} [symbol] Option trading pair
         *
         * @throws {RequiredError}
         */
        ticker24hrPriceChangeStatistics: async (symbol?: string): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (symbol !== undefined && symbol !== null) {
                localVarQueryParameter['symbol'] = symbol;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/eapi/v1/ticker',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
    };
};

/**
 * MarketDataApi - interface
 * @interface MarketDataApi
 */
export interface MarketDataApiInterface {
    /**
     * Test connectivity to the Rest API and get the current server time.
     *
     * Weight(IP): 1
     *
     * @summary Check Server Time
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    checkServerTime(): Promise<RestApiResponse<CheckServerTimeResponse>>;
    /**
     * Current exchange trading rules and symbol information
     *
     * Weight(IP): 1
     *
     * @summary Exchange Information
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    exchangeInformation(): Promise<RestApiResponse<ExchangeInformationResponse>>;
    /**
     * Get historical exercise records.
     *
     * REALISTIC_VALUE_STRICKEN -> Exercised
     * EXTRINSIC_VALUE_EXPIRED -> Expired OTM
     *
     * Weight(IP): 3
     *
     * @summary Historical Exercise Records
     * @param {HistoricalExerciseRecordsRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    historicalExerciseRecords(
        requestParameters?: HistoricalExerciseRecordsRequest
    ): Promise<RestApiResponse<HistoricalExerciseRecordsResponse>>;
    /**
     * Get spot index price for option underlying.
     *
     * Weight(IP): 1
     *
     * @summary Index Price
     * @param {IndexPriceRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    indexPrice(requestParameters: IndexPriceRequest): Promise<RestApiResponse<IndexPriceResponse>>;
    /**
     * Kline/candlestick bars for an option symbol. Klines are uniquely identified by their open time.
     *
     * Weight(IP): 1
     *
     * Notes:
     * - If startTime and endTime are not sent, the most recent klines are returned.
     *
     * @summary Kline/Candlestick Data
     * @param {KlineCandlestickDataRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    klineCandlestickData(
        requestParameters: KlineCandlestickDataRequest
    ): Promise<RestApiResponse<KlineCandlestickDataResponse>>;
    /**
     * Get open interest for specific underlying asset on specific expiration date.
     *
     * Weight(IP): 0
     *
     * @summary Open Interest
     * @param {OpenInterestRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    openInterest(
        requestParameters: OpenInterestRequest
    ): Promise<RestApiResponse<OpenInterestResponse>>;
    /**
     * Option mark price and greek info.
     *
     * Weight(IP): 5
     *
     * @summary Option Mark Price
     * @param {OptionMarkPriceRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    optionMarkPrice(
        requestParameters?: OptionMarkPriceRequest
    ): Promise<RestApiResponse<OptionMarkPriceResponse>>;
    /**
     * Check orderbook depth on specific symbol
     *
     * Weight: limit         | weight
     * ------------  | ------------
     * 5, 10, 20, 50 | 1
     * 100           | 5
     * 500           | 10
     * 1000          | 20
     *
     * @summary Order Book
     * @param {OrderBookRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    orderBook(requestParameters: OrderBookRequest): Promise<RestApiResponse<OrderBookResponse>>;
    /**
     * Get recent block trades
     *
     * Weight(IP): 5
     *
     * @summary Recent Block Trades List
     * @param {RecentBlockTradesListRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    recentBlockTradesList(
        requestParameters?: RecentBlockTradesListRequest
    ): Promise<RestApiResponse<RecentBlockTradesListResponse>>;
    /**
     * Get recent market trades
     *
     * Weight(IP): 5
     *
     * @summary Recent Trades List
     * @param {RecentTradesListRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    recentTradesList(
        requestParameters: RecentTradesListRequest
    ): Promise<RestApiResponse<RecentTradesListResponse>>;
    /**
     * Test connectivity to the Rest API.
     *
     * Weight(IP): 1
     *
     * @summary Test Connectivity
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    testConnectivity(): Promise<RestApiResponse<void>>;
    /**
     * 24 hour rolling window price change statistics.
     *
     * Weight: 1 for a single symbol; 40 when the symbol parameter is omitted
     *
     * @summary 24hr Ticker Price Change Statistics
     * @param {Ticker24hrPriceChangeStatisticsRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApiInterface
     */
    ticker24hrPriceChangeStatistics(
        requestParameters?: Ticker24hrPriceChangeStatisticsRequest
    ): Promise<RestApiResponse<Ticker24hrPriceChangeStatisticsResponse>>;
}

/**
 * Request parameters for historicalExerciseRecords operation in MarketDataApi.
 * @interface HistoricalExerciseRecordsRequest
 */
export interface HistoricalExerciseRecordsRequest {
    /**
     * Underlying asset.
     * @type {string}
     * @memberof MarketDataApiHistoricalExerciseRecords
     */
    readonly underlying?: string;

    /**
     * Start Time, e.g 1593511200000
     * @type {number | bigint}
     * @memberof MarketDataApiHistoricalExerciseRecords
     */
    readonly startTime?: number | bigint;

    /**
     * End Time, e.g 1593512200000
     * @type {number | bigint}
     * @memberof MarketDataApiHistoricalExerciseRecords
     */
    readonly endTime?: number | bigint;

    /**
     * Number of result sets returned
     * @type {number | bigint}
     * @memberof MarketDataApiHistoricalExerciseRecords
     */
    readonly limit?: number | bigint;
}

/**
 * Request parameters for indexPrice operation in MarketDataApi.
 * @interface IndexPriceRequest
 */
export interface IndexPriceRequest {
    /**
     * Underlying asset.
     * @type {string}
     * @memberof MarketDataApiIndexPrice
     */
    readonly underlying: string;
}

/**
 * Request parameters for klineCandlestickData operation in MarketDataApi.
 * @interface KlineCandlestickDataRequest
 */
export interface KlineCandlestickDataRequest {
    /**
     * Option trading pair
     * @type {string}
     * @memberof MarketDataApiKlineCandlestickData
     */
    readonly symbol: string;

    /**
     * Time interval
     * @type {'1m' | '3m' | '5m' | '15m' | '30m' | '1h' | '2h' | '4h' | '6h' | '8h' | '12h' | '1d' | '3d' | '1w' | '1M'}
     * @memberof MarketDataApiKlineCandlestickData
     */
    readonly interval: KlineCandlestickDataIntervalEnum;

    /**
     * Start Time, e.g 1593511200000
     * @type {number | bigint}
     * @memberof MarketDataApiKlineCandlestickData
     */
    readonly startTime?: number | bigint;

    /**
     * End Time, e.g 1593512200000
     * @type {number | bigint}
     * @memberof MarketDataApiKlineCandlestickData
     */
    readonly endTime?: number | bigint;

    /**
     * Number of result sets returned
     * @type {number | bigint}
     * @memberof MarketDataApiKlineCandlestickData
     */
    readonly limit?: number | bigint;
}

/**
 * Request parameters for openInterest operation in MarketDataApi.
 * @interface OpenInterestRequest
 */
export interface OpenInterestRequest {
    /**
     * Underlying asset.
     * @type {string}
     * @memberof MarketDataApiOpenInterest
     */
    readonly underlyingAsset: string;

    /**
     * expiration date
     * @type {string}
     * @memberof MarketDataApiOpenInterest
     */
    readonly expiration: string;
}

/**
 * Request parameters for optionMarkPrice operation in MarketDataApi.
 * @interface OptionMarkPriceRequest
 */
export interface OptionMarkPriceRequest {
    /**
     * Option trading pair
     * @type {string}
     * @memberof MarketDataApiOptionMarkPrice
     */
    readonly symbol?: string;
}

/**
 * Request parameters for orderBook operation in MarketDataApi.
 * @interface OrderBookRequest
 */
export interface OrderBookRequest {
    /**
     * Option trading pair
     * @type {string}
     * @memberof MarketDataApiOrderBook
     */
    readonly symbol: string;

    /**
     * Default:100 Max:1000.Optional value:[10, 20, 50, 100, 500, 1000]
     * @type {number | bigint}
     * @memberof MarketDataApiOrderBook
     */
    readonly limit?: number | bigint;
}

/**
 * Request parameters for recentBlockTradesList operation in MarketDataApi.
 * @interface RecentBlockTradesListRequest
 */
export interface RecentBlockTradesListRequest {
    /**
     * Option trading pair
     * @type {string}
     * @memberof MarketDataApiRecentBlockTradesList
     */
    readonly symbol?: string;

    /**
     * Number of records
     * @type {number | bigint}
     * @memberof MarketDataApiRecentBlockTradesList
     */
    readonly limit?: number | bigint;
}

/**
 * Request parameters for recentTradesList operation in MarketDataApi.
 * @interface RecentTradesListRequest
 */
export interface RecentTradesListRequest {
    /**
     * Option trading pair
     * @type {string}
     * @memberof MarketDataApiRecentTradesList
     */
    readonly symbol: string;

    /**
     * Number of result sets returned
     * @type {number | bigint}
     * @memberof MarketDataApiRecentTradesList
     */
    readonly limit?: number | bigint;
}

/**
 * Request parameters for ticker24hrPriceChangeStatistics operation in MarketDataApi.
 * @interface Ticker24hrPriceChangeStatisticsRequest
 */
export interface Ticker24hrPriceChangeStatisticsRequest {
    /**
     * Option trading pair
     * @type {string}
     * @memberof MarketDataApiTicker24hrPriceChangeStatistics
     */
    readonly symbol?: string;
}

/**
 * MarketDataApi - object-oriented interface
 * @class MarketDataApi
 */
export class MarketDataApi implements MarketDataApiInterface {
    private readonly configuration: ConfigurationRestAPI;
    private localVarAxiosParamCreator;

    constructor(configuration: ConfigurationRestAPI) {
        this.configuration = configuration;
        this.localVarAxiosParamCreator = MarketDataApiAxiosParamCreator(configuration);
    }

    /**
     * Test connectivity to the Rest API and get the current server time.
     *
     * Weight(IP): 1
     *
     * @summary Check Server Time
     * @returns {Promise<RestApiResponse<CheckServerTimeResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/rest-api/market-data#check-server-time Binance API Documentation}
     */
    public async checkServerTime(): Promise<RestApiResponse<CheckServerTimeResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.checkServerTime();
        return sendRequest<CheckServerTimeResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * Current exchange trading rules and symbol information
     *
     * Weight(IP): 1
     *
     * @summary Exchange Information
     * @returns {Promise<RestApiResponse<ExchangeInformationResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/rest-api/market-data#exchange-information Binance API Documentation}
     */
    public async exchangeInformation(): Promise<RestApiResponse<ExchangeInformationResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.exchangeInformation();
        return sendRequest<ExchangeInformationResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * Get historical exercise records.
     *
     * REALISTIC_VALUE_STRICKEN -> Exercised
     * EXTRINSIC_VALUE_EXPIRED -> Expired OTM
     *
     * Weight(IP): 3
     *
     * @summary Historical Exercise Records
     * @param {HistoricalExerciseRecordsRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<HistoricalExerciseRecordsResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/rest-api/market-data#historical-exercise-records Binance API Documentation}
     */
    public async historicalExerciseRecords(
        requestParameters: HistoricalExerciseRecordsRequest = {}
    ): Promise<RestApiResponse<HistoricalExerciseRecordsResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.historicalExerciseRecords(
            requestParameters?.underlying,
            requestParameters?.startTime,
            requestParameters?.endTime,
            requestParameters?.limit
        );
        return sendRequest<HistoricalExerciseRecordsResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * Get spot index price for option underlying.
     *
     * Weight(IP): 1
     *
     * @summary Index Price
     * @param {IndexPriceRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<IndexPriceResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/rest-api/market-data#index-price Binance API Documentation}
     */
    public async indexPrice(
        requestParameters: IndexPriceRequest
    ): Promise<RestApiResponse<IndexPriceResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.indexPrice(
            requestParameters?.underlying
        );
        return sendRequest<IndexPriceResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * Kline/candlestick bars for an option symbol. Klines are uniquely identified by their open time.
     *
     * Weight(IP): 1
     *
     * Notes:
     * - If startTime and endTime are not sent, the most recent klines are returned.
     *
     * @summary Kline/Candlestick Data
     * @param {KlineCandlestickDataRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<KlineCandlestickDataResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/rest-api/market-data#kline-candlestick-data Binance API Documentation}
     */
    public async klineCandlestickData(
        requestParameters: KlineCandlestickDataRequest
    ): Promise<RestApiResponse<KlineCandlestickDataResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.klineCandlestickData(
            requestParameters?.symbol,
            requestParameters?.interval,
            requestParameters?.startTime,
            requestParameters?.endTime,
            requestParameters?.limit
        );
        return sendRequest<KlineCandlestickDataResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * Get open interest for specific underlying asset on specific expiration date.
     *
     * Weight(IP): 0
     *
     * @summary Open Interest
     * @param {OpenInterestRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<OpenInterestResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/rest-api/market-data#open-interest Binance API Documentation}
     */
    public async openInterest(
        requestParameters: OpenInterestRequest
    ): Promise<RestApiResponse<OpenInterestResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.openInterest(
            requestParameters?.underlyingAsset,
            requestParameters?.expiration
        );
        return sendRequest<OpenInterestResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * Option mark price and greek info.
     *
     * Weight(IP): 5
     *
     * @summary Option Mark Price
     * @param {OptionMarkPriceRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<OptionMarkPriceResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/rest-api/market-data#option-mark-price Binance API Documentation}
     */
    public async optionMarkPrice(
        requestParameters: OptionMarkPriceRequest = {}
    ): Promise<RestApiResponse<OptionMarkPriceResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.optionMarkPrice(
            requestParameters?.symbol
        );
        return sendRequest<OptionMarkPriceResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * Check orderbook depth on specific symbol
     *
     * Weight: limit         | weight
     * ------------  | ------------
     * 5, 10, 20, 50 | 1
     * 100           | 5
     * 500           | 10
     * 1000          | 20
     *
     * @summary Order Book
     * @param {OrderBookRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<OrderBookResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/rest-api/market-data#order-book Binance API Documentation}
     */
    public async orderBook(
        requestParameters: OrderBookRequest
    ): Promise<RestApiResponse<OrderBookResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.orderBook(
            requestParameters?.symbol,
            requestParameters?.limit
        );
        return sendRequest<OrderBookResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * Get recent block trades
     *
     * Weight(IP): 5
     *
     * @summary Recent Block Trades List
     * @param {RecentBlockTradesListRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<RecentBlockTradesListResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/rest-api/market-data#recent-block-trades-list Binance API Documentation}
     */
    public async recentBlockTradesList(
        requestParameters: RecentBlockTradesListRequest = {}
    ): Promise<RestApiResponse<RecentBlockTradesListResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.recentBlockTradesList(
            requestParameters?.symbol,
            requestParameters?.limit
        );
        return sendRequest<RecentBlockTradesListResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * Get recent market trades
     *
     * Weight(IP): 5
     *
     * @summary Recent Trades List
     * @param {RecentTradesListRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<RecentTradesListResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/rest-api/market-data#recent-trades-list Binance API Documentation}
     */
    public async recentTradesList(
        requestParameters: RecentTradesListRequest
    ): Promise<RestApiResponse<RecentTradesListResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.recentTradesList(
            requestParameters?.symbol,
            requestParameters?.limit
        );
        return sendRequest<RecentTradesListResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * Test connectivity to the Rest API.
     *
     * Weight(IP): 1
     *
     * @summary Test Connectivity
     * @returns {Promise<RestApiResponse<void>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/rest-api/market-data#test-connectivity Binance API Documentation}
     */
    public async testConnectivity(): Promise<RestApiResponse<void>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.testConnectivity();
        return sendRequest<void>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * 24 hour rolling window price change statistics.
     *
     * Weight: 1 for a single symbol; 40 when the symbol parameter is omitted
     *
     * @summary 24hr Ticker Price Change Statistics
     * @param {Ticker24hrPriceChangeStatisticsRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<Ticker24hrPriceChangeStatisticsResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof MarketDataApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/rest-api/market-data#ticker24hr-price-change-statistics Binance API Documentation}
     */
    public async ticker24hrPriceChangeStatistics(
        requestParameters: Ticker24hrPriceChangeStatisticsRequest = {}
    ): Promise<RestApiResponse<Ticker24hrPriceChangeStatisticsResponse>> {
        const localVarAxiosArgs =
            await this.localVarAxiosParamCreator.ticker24hrPriceChangeStatistics(
                requestParameters?.symbol
            );
        return sendRequest<Ticker24hrPriceChangeStatisticsResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }
}

export enum KlineCandlestickDataIntervalEnum {
    INTERVAL_1m = '1m',
    INTERVAL_3m = '3m',
    INTERVAL_5m = '5m',
    INTERVAL_15m = '15m',
    INTERVAL_30m = '30m',
    INTERVAL_1h = '1h',
    INTERVAL_2h = '2h',
    INTERVAL_4h = '4h',
    INTERVAL_6h = '6h',
    INTERVAL_8h = '8h',
    INTERVAL_12h = '12h',
    INTERVAL_1d = '1d',
    INTERVAL_3d = '3d',
    INTERVAL_1w = '1w',
    INTERVAL_1M = '1M',
}
